import type { FC, PropsWithChildren } from 'react';
import { Box, HStack, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { CheckCircleIcon, XCircleIcon } from '../../../../components/icons';
import { Text } from '../../../../ui-components/typography/text/Text';
import { validatePassword } from '../../../../utils/password-utils';

type PasswordRequirementProps = PropsWithChildren<{
    isValid: boolean | undefined;
    type: 'length' | 'letter' | 'number';
    showInvalidIcon?: boolean;
}>;

/**
 * UI component for styling password requirement line
 */
const PasswordRequirement: FC<PasswordRequirementProps> = ({ isValid, type, children }) => {
    const iconStyles = {
        color: isValid ? 'slt-success-green.1000' : 'white',
    };
    const dataTestIdLabel = `register-password-label-${type}`;
    const dataTestIdValidIcon = `register-password-is-valid-icon-${type}`;
    const dataTestIdInvalidIcon = `register-password-is-invalid-icon-${type}`;
    return (
        <HStack spacing="8px">
            <Box w="16px" h="16px" display="flex" alignItems="center" justifyContent="center">
                {isValid === true ? (
                    <CheckCircleIcon {...iconStyles} data-testid={dataTestIdValidIcon} />
                ) : null}
                {isValid === false ? (
                    <XCircleIcon {...iconStyles} data-testid={dataTestIdInvalidIcon} />
                ) : null}
            </Box>
            <Text type="body" size="small" data-testid={dataTestIdLabel}>
                {children}
            </Text>
        </HStack>
    );
};

PasswordRequirement.defaultProps = {
    showInvalidIcon: false,
};

interface PasswordRequirementsProps {
    /** The password to check against */
    value: string;
}

/**
 * Renders a list of password requirments. Each requirement line toggles to its `isValid`
 * state when the given password value meets the associated critieria.
 */
const PasswordRequirements: FC<PasswordRequirementsProps> = ({ value }) => {
    const pwValidations = value.length > 0 ? validatePassword(value) : undefined;

    return (
        <VStack spacing={2} align="stretch">
            <PasswordRequirement type="length" isValid={pwValidations?.hasMinChars}>
                <FormattedMessage
                    id="password_requirements.error.six_characters_minimum"
                    defaultMessage="6 characters minimum"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement type="letter" isValid={pwValidations?.hasAnyLetter}>
                <FormattedMessage
                    id="password_requirements.error.one_letter"
                    defaultMessage="At least 1 letter"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement type="number" isValid={pwValidations?.hasNumber}>
                <FormattedMessage
                    defaultMessage="At least 1 number"
                    description="Password requirement"
                    id="password_requirements.error.one_number"
                />
            </PasswordRequirement>
        </VStack>
    );
};

export { PasswordRequirements };
